'use client';

import { create } from 'zustand';

export type NoticeType = 'error' | 'success' | 'info' | 'warning';
export type Notice = {
    id: string;
    type: NoticeType;
    message: string;
    title: string;
    ttl?: number;
};

type State = {
    current?: Notice;
    show: (notice: Omit<Notice, 'id'> & { id?: string }) => void;
    hide: () => void;
};

const TIME_TO_HIDE = 5000;
let timerId: ReturnType<typeof setTimeout> | undefined;

export const useNotificationStore = create<State>()(set => ({
    current: undefined,
    show: ({ id, type, message, title, ttl = TIME_TO_HIDE }) => {
        const notice: Notice = { id: id ?? crypto.randomUUID(), type, message, title, ttl };
        if (timerId) clearTimeout(timerId);

        set({ current: notice });
        if (ttl) timerId = setTimeout(() => set({ current: undefined }), ttl);
    },
    hide: () => {
        if (timerId) clearTimeout(timerId);
        set({ current: undefined });
    },
}));

// Helper functions to use the store. Only a client story!

type HandlerProp = {
    title: string;
    message: string;
    ttl?: number;
};

export const showError = ({ message, title, ttl }: HandlerProp) =>
    useNotificationStore.getState().show({ type: 'error', message, title, ttl });

export const showSuccess = ({ message, title, ttl }: HandlerProp) =>
    useNotificationStore.getState().show({ type: 'success', message, title, ttl });

export const showInfo = ({ message, title, ttl }: HandlerProp) =>
    useNotificationStore.getState().show({ type: 'info', message, title, ttl });

export const showWarning = ({ message, title, ttl }: HandlerProp) =>
    useNotificationStore.getState().show({ type: 'warning', message, title, ttl });

export const hideNotice = () => useNotificationStore.getState().hide();

export const hideSuccess = () => {
    const { current, hide } = useNotificationStore.getState();
    if (current?.type === 'success') {
        hide();
    }
};
